/**
 * Promotion detail — /promotion/:ref and /pk/en/promotion/:ref, an id or a
 * slug (web plan W0.16, `useEntityPage`). Views and the share link use the
 * promotion itself: its id and its `urlPath`.
 *
 * lg+: two-pane layout — media viewer left, sticky details panel right.
 * Below lg the panes stack (viewer first, then details).
 */
import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import {
  BadgeCheck,
  Building2,
  Calendar,
  ExternalLink,
  Eye,
  MousePointerClick,
  Share2,
} from 'lucide-react';
import { usePromotionDetail } from '@/hooks/useAppData';
import { useEntityPage } from '@/hooks/useEntityPage';
import { useFollowToggle } from '@/hooks/useFollowToggle';
import { useCatalogPath, useRouteLocale } from '@/hooks/useRouteLocale';
import { usePromotionRails } from '@/hooks/usePromotionRails';
import { useTrackView } from '@/hooks/useTrackView';
import { useRenderNow } from '@/hooks/useRenderNow';
import { PageHead } from '@/components/seo/PageHead';
import { useTranslate } from '@/i18n/LocaleLanguage';
import {
  Badge,
  Button,
  Chip,
  Img,
  SectionHeader,
  Skeleton,
  StatPill,
  isPublicCount,
} from '@/components/ui';
import { Breadcrumbs } from '@/components/ui/Breadcrumbs';
import { DetailLoadError, DetailNotFound } from '@/components/detail/DetailStatusViews';
import { FollowForAlerts } from '@/components/detail/FollowForAlerts';
import {
  PromotionEndedBanner,
  PromotionRail,
  WhereItsValid,
} from '@/components/detail/PromotionRails';
import { CategoryChips } from '@/components/category/CategoryChips';
import { PromotionViewer } from '@/pages/detail/PromotionViewer';
import { shareUrl } from '@/utils/share';
import { ImagePresets } from '@/utils/safeImageUrl';
import { promotionMerchant } from '@/seo/structuredData';
import { entityPath, shareLink } from '@/seo/urls';
import { promotionNotice } from '@/seo/lifecycle';
import { offerLink } from '@/seo/offer';
import { promotionPageHead } from '@/seo/pageHeads';
import { promotionH1 } from '@/seo/templates';
import { cleanDescription, cleanName, displayName } from '@/seo/text';
import { detailSeoOptions } from '@/utils/detailStatus';

function PageSkeleton() {
  return (
    <div className="grid gap-8 lg:grid-cols-[1fr_420px]" aria-hidden="true">
      {/* Viewer pane */}
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <Skeleton className="h-10 w-56" />
          <Skeleton className="h-10 w-10" />
        </div>
        <Skeleton className="h-[60vh] w-full rounded-2xl md:h-[70vh]" />
      </div>
      {/* Details pane */}
      <div className="space-y-5">
        <div className="flex items-center justify-between gap-3">
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-4 w-28" />
        </div>
        <div className="flex items-start justify-between gap-3">
          <Skeleton className="h-9 w-3/5" />
          <Skeleton className="h-9 w-24" />
        </div>
        <Skeleton className="h-5 w-32" />
        <Skeleton className="h-[72px] w-full rounded-2xl" />
        <Skeleton className="h-24 w-full rounded-2xl" />
        <div className="grid grid-cols-2 gap-3">
          <Skeleton className="h-32 w-full rounded-2xl" />
          <Skeleton className="h-32 w-full rounded-2xl" />
        </div>
      </div>
    </div>
  );
}

export default function PromotionDetailPage() {
  const navigate = useNavigate();
  // Home: `/pk/en` once the URL move is live (web plan W2.1).
  const home = useCatalogPath()('/');
  const { item: promo, status, errorCode, refetch, redirect } = useEntityPage(usePromotionDetail);
  useTrackView('promotion', promo?.id, status);
  // "More from {Brand}" / "Similar {Category} deals" (web plan W0.9), asked
  // for only once the promotion itself is known.
  const rails = usePromotionRails(status === 'ok' ? promo : null);
  // The brand's follow toggle (the brand page's own), for "Follow {Brand}
  // for alerts" on an upcoming or ended promotion (master D-4).
  const followBrand = useFollowToggle('brand', status === 'ok' ? promo?.brand : null);

  // One moment for everything the dates decide on this render: the robots,
  // the title, the JSON-LD availability and the visible notices agree (the
  // server's render time while hydrating, so they match its HTML).
  const now = useRenderNow();
  // Title ("{Brand} Sale: {headline} (Ends {D Mon})"), description, H1,
  // breadcrumbs and the D-4 robots from the promotion formula (web plan W0.6):
  // upcoming, active and ended ≤ 7 days `index, …, unavailable_after: {end +
  // 7 d}`, ended 8–90 days an explicit `noindex, follow` (W0.9). JSON-LD
  // (W0.7): a WebPage whose mainEntity is the Offer, its availability from the
  // dates (never InStock once ended). The SEO server builds the same head from
  // the same query (web plan W1.3).
  const t = useTranslate();
  const locale = useRouteLocale();
  const head = promo && status === 'ok' ? promotionPageHead(promo, { now, t, locale }) : null;
  const pageHead = (
    <PageHead
      head={head}
      // Loading / not found / failed load (web plan W0.2): "Promotion ·
      // PromoStork" or "Promotion not found" + noindex, never the homepage
      // title. Kept last.
      {...(status === 'ok' ? {} : detailSeoOptions('promotion', status))}
    />
  );

  // Transient "Link copied!" feedback after a clipboard-fallback share.
  const [copied, setCopied] = useState(false);
  const copiedTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(copiedTimer.current), []);

  const handleShare = async () => {
    if (!promo) return;
    try {
      const result = await shareUrl(promo.name, shareLink(entityPath(promo, 'promotion')), {
        contentType: 'promotion',
        itemId: promo.id,
      });
      if (result === 'copied') {
        setCopied(true);
        window.clearTimeout(copiedTimer.current);
        copiedTimer.current = window.setTimeout(() => setCopied(false), 2000);
      }
    } catch {
      // Clipboard unavailable (insecure context / permission) — fail silently.
    }
  };

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate(home);
  };

  if (redirect) return <Navigate replace to={redirect.to} state={redirect.state} />;

  if (status === 'loading') {
    return (
      <div className="mx-auto max-w-7xl px-4 py-6 md:py-8">
        {pageHead}
        {/* The breadcrumb row's box (one text-sm line + mb-4), as loaded. */}
        <Skeleton className="mb-4 h-5 w-72 max-w-full rounded-md" />
        <PageSkeleton />
      </div>
    );
  }

  if (status === 'not_found') {
    return (
      <div className="mx-auto max-w-7xl px-4 py-6 md:py-8">
        {pageHead}
        <DetailNotFound kind="promotion" />
      </div>
    );
  }

  if (status === 'error' || !promo) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-6 md:py-8">
        {pageHead}
        <DetailLoadError
          kind="promotion"
          errorCode={errorCode}
          onRetry={refetch}
          onBack={handleBack}
        />
      </div>
    );
  }

  // Dates only (master D-4), never the stored status or `isUpcoming`: the
  // backend's status getter still reads UPCOMING / EXPIRED, and showing it
  // put a raw second badge next to "Upcoming".
  const merchant = promotionMerchant(promo);
  const merchantLink = merchant
    ? { name: merchant.name, path: entityPath(merchant.ref, merchant.type) }
    : null;
  const notice = promotionNotice(promo, merchant?.name, now, locale);
  const brandName = promo.brand ? cleanName(promo.brand.name) : '';
  // The brand row's label and logo alt: cleaned, or the stored name when
  // cleaning leaves nothing (the rails and the follow card need a clean one).
  const brandLabel = displayName(promo.brand?.name);
  const viewCount = promo.viewCounts ?? 0;
  const clickCount = promo.clickCounts ?? 0;
  const showViews = isPublicCount(viewCount);
  const showClicks = isPublicCount(clickCount);
  const description = cleanDescription(promo.description, { max: Infinity, keepLineBreaks: true });
  // Backend B3.5: the terms as typed (plain text, line breaks kept — URLs and
  // "#" references are part of legal text, so no description clean-up), and
  // the brand's own page for the offer while it is not over.
  const terms = promo.terms?.trim().replace(/\n{3,}/g, '\n\n') ?? '';
  const offer = notice.phase !== 'ended' ? offerLink(promo.offerUrl) : null;
  const stores = promo.stores ?? [];
  const shops = promo.shops ?? [];
  const categories = promo.categories ?? [];
  const tags = promo.tags ?? [];

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 md:py-8">
      {pageHead}
      {/* Home › Brands › {Brand} › {headline} (Shops › {Shop} for a
          shop-only offer): the BreadcrumbList's own array (web plan W0.8). */}
      <Breadcrumbs crumbs={head?.breadcrumbs} className="mb-4" />

      {/* Ended: "This {Brand} offer ended on {date}" above the fold, with a
          link to the brand's current offers (master D-4). */}
      {notice.endedNotice && (
        <PromotionEndedBanner notice={notice.endedNotice} merchant={merchantLink} className="mb-6" />
      )}

      <div className="grid gap-8 lg:grid-cols-[1fr_420px]">
        {/* Left pane — media viewer */}
        <div className="min-w-0 animate-fade-up">
          {/* Image alts and the full-screen title use the H1's headline (the
              backend's clean headline, B3.5), else the cleaned name. */}
          <PromotionViewer
            media={promo.media ?? []}
            name={displayName(promo.headline) || displayName(promo.name)}
          />
        </div>

        {/* Right pane — details */}
        <aside className="min-w-0 space-y-5 self-start animate-fade-up lg:sticky lg:top-24">
          {/* Brand row + validity hint (running offers; the badge below
              carries the date of an upcoming or ended one) */}
          {(promo.brand || notice.validTill) && (
            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
              {promo.brand ? (
                <Link
                  to={entityPath(promo.brand, 'brand')}
                  className="group flex min-w-0 items-center gap-2"
                >
                  <Img
                    media={promo.brand.logo}
                    transform={ImagePresets.thumb}
                    // Decorative: the brand's name is the link's text beside it
                    // (image-redundant-alt; a screen reader would say it twice).
                    alt=""
                    className="h-6 w-6 shrink-0 rounded-full border border-line"
                    fallback={
                      <Building2 size={14} className="text-ink-tertiary" aria-hidden="true" />
                    }
                  />
                  <span className="truncate text-sm font-semibold text-ink transition-all duration-200 group-hover:text-primary">
                    {brandLabel}
                  </span>
                </Link>
              ) : (
                <span aria-hidden="true" />
              )}
              {notice.validTill && (
                <span className="shrink-0 text-xs text-ink-secondary">{notice.validTill}</span>
              )}
            </div>
          )}

          {/* Title + share */}
          <div>
            <div className="flex items-start justify-between gap-3">
              <h1 className="min-w-0 text-2xl font-extrabold tracking-tight text-ink md:text-3xl">
                {head?.h1 ?? promotionH1(promo, { t })}
              </h1>
              <Button
                variant="primary"
                size="sm"
                onClick={handleShare}
                leftIcon={<Share2 size={16} aria-hidden="true" />}
                className="shrink-0"
              >
                Share
              </Button>
            </div>
            {copied && (
              <p role="status" className="mt-1.5 text-xs font-semibold text-success">
                Link copied!
              </p>
            )}
          </div>

          {/* Status badges: one lifecycle badge from the dates ("Starts
              {date}" / "Ended {date}"; none while running) + verified. */}
          {(notice.badge || promo.verified === 'VERIFIED') && (
            <div className="flex flex-wrap items-center gap-2.5">
              {notice.badge && (
                <Badge color={notice.phase === 'upcoming' ? 'warning' : 'neutral'}>
                  {notice.badge}
                </Badge>
              )}
              {promo.verified === 'VERIFIED' && (
                <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-success">
                  <BadgeCheck size={18} aria-hidden="true" />
                  Verified Promotion
                </span>
              )}
            </div>
          )}

          {/* Not running (yet, or any more): "Follow {Brand} for alerts"
              (master D-4). A shop-only promotion has no brand to follow. */}
          {notice.phase !== 'active' && promo.brand && brandName && (
            <FollowForAlerts name={brandName} follow={followBrand} />
          )}

          {/* Validity period card, in the locale's time zone (the same days
              as the title and the badge, wherever the visitor is). */}
          {(notice.period || notice.lastVerified) && (
            <div className="flex items-center gap-3 rounded-2xl border border-line p-4">
              <span className="shrink-0 rounded-xl bg-primary/10 p-2">
                <Calendar size={20} className="text-primary" aria-hidden="true" />
              </span>
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-wide text-ink-secondary">
                  Validity Period
                </p>
                {notice.period && <p className="font-semibold text-ink">{notice.period}</p>}
                {/* No end date: when staff last confirmed it (backend B3.5). */}
                {notice.lastVerified && (
                  <p className="mt-0.5 text-xs text-ink-secondary">{notice.lastVerified}</p>
                )}
              </div>
            </div>
          )}

          {/* The brand's own page for the offer (backend B3.5 offerUrl): an
              external link that passes no ranking (nofollow) and gets no
              handle on this tab (noopener); the referrer is kept so the
              brand sees the visit came from PromoStork. */}
          {offer && (
            <a
              href={offer.href}
              target="_blank"
              rel="nofollow noopener"
              // The primary Button's colours, but the long host may wrap on a phone.
              className="flex w-full items-center justify-center gap-2 break-words rounded-xl bg-primary px-4 py-3 text-center text-sm font-bold text-white transition-all duration-200 hover:bg-primary-dark"
            >
              <ExternalLink size={16} aria-hidden="true" />
              {t('promotion:page.offerLink', { host: offer.host })}
              <span className="sr-only"> {t('promotion:page.offerLinkNewTab')}</span>
            </a>
          )}

          {/* Description: the first section under the H1, so an <h2>. Shown
              through cleanDescription (styled Unicode folded, emoji, URLs and
              hashtags stripped, paragraphs kept), like the meta description. */}
          {description && (
            <div>
              <h2 className="font-bold text-ink">About this offer</h2>
              <p className="mt-1.5 whitespace-pre-line text-sm leading-relaxed text-ink-secondary">
                {description}
              </p>
            </div>
          )}

          {/* Terms & conditions (backend B3.5), only when entered. */}
          {terms && (
            <div>
              <h2 className="font-bold text-ink">{t('promotion:page.terms')}</h2>
              <p className="mt-1.5 whitespace-pre-line text-sm leading-relaxed text-ink-secondary">
                {terms}
              </p>
            </div>
          )}

          {/* Stats: popularity counters only from PUBLIC_COUNTER_MIN up (W0.6). */}
          {(showViews || showClicks) && (
            <div className="flex flex-wrap gap-3">
              {showViews && <StatPill icon={Eye} value={viewCount} label="Views" />}
              {showClicks && (
                <StatPill icon={MousePointerClick} value={clickCount} label="Clicks" />
              )}
            </div>
          )}

          {/* Where it's valid: the backend's availability line (B3.5), then
              the stores and shops it names, or for a brand-wide offer its
              brand's cities + the brand page (W0.9); an online-only offer
              lists no stores. */}
          <WhereItsValid
            stores={stores}
            shops={shops}
            brand={promo.brand}
            scope={promo.availabilityScope}
            merchantName={merchant?.name}
          />

          {/* Categories — clickable, open search filtered by branch */}
          {categories.length > 0 && (
            <div className="space-y-3">
              <SectionHeader title="Categories" />
              <CategoryChips categories={categories} segment="promotions" />
            </div>
          )}

          {/* Tags — free-text labels, not clickable */}
          {tags.length > 0 && (
            <div className="space-y-3">
              <SectionHeader title="Tags" />
              <div className="flex flex-wrap gap-2">
                {tags.map((tag) => (
                  <Chip key={tag.id} label={tag.title} color="neutral" outline />
                ))}
              </div>
            </div>
          )}
        </aside>
      </div>

      {/* Cross-link rails on every promotion page, whatever its phase (web
          plan W0.9): the brand's other listed offers (an ended page's "live
          offers", master D-4) with a link to its page, then similar offers
          from other brands in the same L2 category. Plain <a> cards. */}
      {promo.brand && brandName && (
        <PromotionRail
          className="mt-12"
          title={`More from ${brandName}`}
          promotions={rails.brand}
          more={{ to: entityPath(promo.brand, 'brand'), label: `All ${brandName} offers` }}
          emptyText={rails.brandSettled ? `No other ${brandName} offers right now.` : null}
        />
      )}
      {rails.category && rails.similar.length > 0 && (
        <PromotionRail
          className="mt-10"
          title={`Similar ${rails.category.title} deals`}
          promotions={rails.similar}
        />
      )}
    </div>
  );
}
